import React from 'react';
import { observer } from 'mobx-react';
import { Button } from '@this/shared/ui/inputs/button';
import { Input } from '@this/shared/ui/inputs/input';
import { Select } from '@this/components/shared/ui/inputs/select';
import { FormControl, InputLabel } from '@this/shared/ui/inputs/form_control';
import type Prefecture from '@this/domain/prefecture';
import type Station from '@this/domain/station';

function getStyles() {
  return {
    hotelMasterInput: {
      padding: '8px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '14px',
      width: '200px'
    } as React.CSSProperties,
    inputRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
      gap: '10px'
    } as React.CSSProperties,
    buttonRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      fontSize: '14px',
      marginTop: '10px'
    } as React.CSSProperties,
    label: {
      minWidth: '120px',
      fontWeight: 'bold',
      fontSize: '14px'
    } as React.CSSProperties
  } as const;
}

interface StationFormProps {
  station: Station;
  onSubmit: () => void;
  onCancel: () => void;
  error: string | null;
  submitLabel: string;
  cancelLabel: string;
  prefectures: Prefecture[];
  prefecturesLoading: boolean;
}

const StationForm: React.FC<StationFormProps> = ({
  station,
  onSubmit,
  onCancel,
  error,
  submitLabel,
  cancelLabel,
  prefectures,
  prefecturesLoading
}) => {
  const styles = getStyles();

  return (
    <div>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="stationName">駅名</InputLabel>
        <Input
          id="stationName"
          value={station.stationName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            station.stationName = e.target.value;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="prefectureId">都道府県</InputLabel>
        <Select
          id="prefectureId"
          value={station.prefectureId?.toString() || ''}
          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
            const value = e.target.value as string;
            if (value) {
              const selectedPrefecture = prefectures.find(p => p.id === Number(value));
              station.prefectureId = Number(value);
              station.prefectureName = selectedPrefecture?.name || '';
            } else {
              station.prefectureId = undefined;
              station.prefectureName = '';
            }
          }}
          disabled={prefecturesLoading}
        >
          <option value="">選択してください</option>
          {prefectures.map(prefecture => (
            <option key={prefecture.id} value={prefecture.id}>
              {prefecture.name}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="post">郵便番号</InputLabel>
        <Input
          id="post"
          value={station.post || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            station.post = e.target.value;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="add">住所</InputLabel>
        <Input
          id="add"
          value={station.add || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            station.add = e.target.value;
          }}
        />
      </FormControl>
      <FormControl fullWidth style={{ marginBottom: '10px' }}>
        <InputLabel htmlFor="lon">経度</InputLabel>
        <Input
          id="lon"
          type="number"
          value={station.lon?.toString() || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            station.lon = Number(e.target.value);
          }}
        />
      </FormControl>
      <FormControl fullWidth>
        <InputLabel htmlFor="lat">緯度</InputLabel>
        <Input
          id="lat"
          type="number"
          value={station.lat?.toString() || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            station.lat = Number(e.target.value);
          }}
        />
      </FormControl>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.buttonRow}>
        <Button onClick={onSubmit}>{submitLabel}</Button>
        <Button color="sub" onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    </div>
  );
};

export default observer(StationForm);
